'use client';

import RichText from 'components/rich-text';
import MarkdownParser from 'components/rich-text/markdown-parser';
import RightArrow from 'assets/icons/arrows/right-arrow.svg';
import LeftArrow from 'assets/icons/arrows/left-arrow.svg';
import styles from './inline-announcements.module.scss';
import { useState } from 'react';
import { getUrlString } from 'helpers/helpers';
import LinkButton from 'components/controls/links/link-button';
import ChevronR from 'assets/icons/chevron-r.svg';
import { type SanityAnnouncement } from '@metmuseum/types';
import { Eyebrow } from 'components/eyebrow';

const colors = [
	styles.indigoBackground,
	styles.orangeBackground,
	styles.blueBackground,
	styles.redBackground,
	styles.yellowBackground,
	styles.greenBackground,
];

const getColor = (index) => {
	return colors[index % colors.length];
};

const isValidAnnouncement = (announcement: SanityAnnouncement) => {
	if (!announcement?.title) return false;
	// Announcements without a valid URL (e.g. ref to unpublished doc) may break the component
	const url = getUrlString(announcement);
	return !!url;
};

const InlineAnnouncement = (props: IProps) => {
	const rawAnnouncements = props.announcements;
	const announcements = rawAnnouncements?.filter(isValidAnnouncement) || [];

	const arrowsVisible = announcements?.length > 1;
	const firstAnnouncement = announcements ? announcements[0] : null;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [selectedAnnouncement, setSelectedAnnouncement] =
		useState(firstAnnouncement);
	const [linkUrl, setLinkUrl] = useState(getUrlString(firstAnnouncement));

	const handleLeftArrowClick = () => {
		const newIndex =
			selectedIndex != 0 ? selectedIndex - 1 : announcements.length - 1;
		const newAnnouncement = announcements[newIndex];
		setSelectedIndex(newIndex);
		setSelectedAnnouncement(newAnnouncement);
		setLinkUrl(getUrlString(newAnnouncement));
	};

	const handleRightArrowClick = () => {
		const newIndex =
			selectedIndex != announcements.length - 1 ? selectedIndex + 1 : 0;
		const newAnnouncement = announcements[newIndex];
		setSelectedIndex(newIndex);
		setSelectedAnnouncement(announcements[newIndex]);
		setLinkUrl(getUrlString(newAnnouncement));
	};

	if (!announcements?.length) return null;

	return (
		<div
			className={`${styles.inlineAnnouncement} ${getColor(selectedIndex)}`}
			key={selectedAnnouncement._id}
		>
			<div className={styles.leftButtonContainer}>
				{arrowsVisible && (
					<button
						onClick={handleLeftArrowClick}
						className={styles.announcementButton}
						aria-label="Previous Announcement"
					>
						<LeftArrow />
					</button>
				)}
			</div>
			<div className={styles.eyebrowContainer}>
				<Eyebrow text={'FEATURED'} />
			</div>
			<div className={styles.titleContainer}>
				<h3 className={styles.title}>
					<MarkdownParser>{selectedAnnouncement.title}</MarkdownParser>
				</h3>
			</div>
			<div className={styles.announcementContainer}>
				<div className={styles.announcementText}>
					<RichText value={selectedAnnouncement.description} />
				</div>
				<div className={styles.linkContainer}>
					<LinkButton
						href={linkUrl}
						text={selectedAnnouncement.cta}
						rightIcon={<ChevronR />}
					/>
				</div>
			</div>
			<div className={styles.rightButtonContainer}>
				{arrowsVisible && (
					<button
						onClick={handleRightArrowClick}
						className={styles.announcementButton}
						aria-label="Next Announcement"
					>
						<RightArrow />
					</button>
				)}
			</div>
		</div>
	);
};

export interface IProps {
	announcements: SanityAnnouncement[];
}

export default InlineAnnouncement;
